import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import Layout from 'components/Layout';
import Link from 'components/Link';
import TooltipWrapper from 'components/Tooltip';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';
import {onApiFormError} from 'utils/request/result';

import EmailSentSuccess from '../EmailSentSuccess';
import RegistrationInfo from '../RegistrationInfo';
import {usePostDXEmail} from './services';

type EmailFormProps = {
    clientName: string;
    code: string;
    instructionUrl: string | undefined;
    partnerName: string;
};

type FormData = FormFields<{
    email: string;
    isAgreed: boolean;
}>;

const EmailForm: FC<EmailFormProps> = ({
    clientName,
    code,
    instructionUrl,
    partnerName,
}) => {
    const methods = useForm<FormData>({
        defaultValues: {
            email: '',
            isAgreed: false,
        },
    });
    const {getValues, watch} = methods;
    watch('email');
    watch('isAgreed');

    const submitEmail = usePostDXEmail();
    const {handleSubmit} = methods;
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const [email, setEmail] = useState('');
    const onSubmit = async ({email}: FormData) => {
        await submitEmail
            .mutateAsync({code, email})
            .then(() => {
                setEmail(email);
                setShowSuccessMessage(true);
            })
            .catch(onApiFormError(methods));
    };

    const isEmailSet = () => !!getValues().email;
    const isChecked = () => !!getValues().isAgreed;
    const isFormValid = () => isEmailSet() && isChecked();

    const getTooltipMessage = () => {
        if (!isEmailSet()) {
            return t`Please enter a valid email`;
        }
        if (!isChecked) {
            return t`You must agree to the MakeLeaps Corp. Privacy Policy.`;
        }
        return '';
    };

    const language = getPreferredLanguage();

    const aboutUsLink = (
        <Link
            href={
                language === 'ja'
                    ? 'https://www.makeleaps.com/about-us/'
                    : 'https://www.makeleaps.com/en/about-us/'
            }
            isOpenInNewTab={true}
            key="privacy-policy"
        >
            {t`MakeLeaps Corp.`}
        </Link>
    );

    const linkAgreement = (
        <Link
            href="https://www.makeleaps.com/プライバシーポリシー/"
            isOpenInNewTab={true}
            key="privacy-policy"
        >
            {t`Privacy Policy of MakeLeaps Corp.`}
        </Link>
    );
    return (
        <div>
            {showSuccessMessage ? (
                <EmailSentSuccess code={code} email={email} />
            ) : (
                <FormProvider {...methods}>
                    <h1 className="my-2">{t`Register your email address below`}</h1>
                    <p className="text-base">
                        {t`Thank you for your code. Please confirm your details below and then input the email address you wish to register with the requesting organization.`}
                    </p>
                    <p className="pb-6 text-base">
                        {t`Please note you can only register one address.`}
                    </p>
                    <div className="pb-6">
                        <RegistrationInfo
                            clientName={clientName}
                            instructionUrl={instructionUrl}
                            partnerName={partnerName}
                        />
                    </div>
                    <form className="gap-y-2" onSubmit={handleSubmit(onSubmit)}>
                        <Layout.Stacked className="hide-required">
                            <div>
                                <Field
                                    className="flex flex-col sm:flex-row"
                                    name="email"
                                    type="input"
                                >
                                    <InputEmail
                                        // eslint-disable-next-line jsx-a11y/no-autofocus
                                        autoFocus={true}
                                        className="grow"
                                        inputMode="email"
                                        label={t`Email address to register`}
                                        name="email"
                                        required={true}
                                        requiredType="hidden"
                                    />
                                </Field>
                            </div>
                        </Layout.Stacked>
                        <p className="pb-6 text-sm text-light-secondary">{jt`The email address(es) submitted here are provided to the contracting party of MakeLeaps (${partnerName}) and are subject to use by ${aboutUsLink} per its Privacy Policy. Management of personal information at MakeLeaps Corp. is the responsibility of the Chief Executive Officer.`}</p>
                        <div className="pb-6">
                            <Checkbox
                                label={jt`I agree to the ${linkAgreement} (Japanese only).`}
                                name="isAgreed"
                                required="You must agree"
                            />
                        </div>
                        <div className="flex flex-row justify-end gap-4">
                            <TooltipWrapper message={getTooltipMessage()}>
                                <SubmitButton
                                    aria-label="send-email"
                                    className="mb-auto align-top sm:ml-3"
                                    disabled={!isFormValid()}
                                    kind="primary"
                                >
                                    {t`Send me a Validation Link`}
                                </SubmitButton>
                            </TooltipWrapper>
                        </div>
                    </form>
                    <FormResult className="mt-4" />
                </FormProvider>
            )}
        </div>
    );
};

export default EmailForm;
